// Frontend access model — mirrors the backend RBAC. The backend guard is the
// real boundary; this gates the sidebar + action buttons + route navigation
// from the logged-in user's effective permission set (see usePermissions).

// Mirrors backend PermissionsService.isAdmin() — 'school_admin' is the
// custom role key every migrated school's admin holds (see
// docs/DEVIATIONS.md "RBAC empty-permissions" entry).
export const ADMIN_ROLES = ['owner', 'admin', 'manager', 'school_admin'];

export function isAdminRole(role?: string): boolean {
  return !!role && ADMIN_ROLES.includes(role);
}

export type PermAction = 'list' | 'create' | 'delete' | 'approve';

/**
 * Permission-gated module path-prefixes (mirror of backend PERMISSION_MODULES
 * keys) — for paths a SCHOOL ADMIN might navigate to. `/material-items` is
 * deliberately NOT here: that catalog is Super-Admin-only (its own nav lives
 * in SuperadminLayout, gated by `SuperadminRoute` alone, same as every other
 * superadmin-only page) — registering it here would let a school admin's
 * (broad, ALL_PERMISSIONS-based) permission set show a nav link that 403s
 * the moment they click it.
 */
export const MODULE_KEYS = [
  '/setup/academic-years',
  '/setup/classes',
  '/setup/class-subjects',
  '/setup/section-teachers',
  '/setup/subjects',
  '/setup/lessons',
  '/promotion',
  '/academic-calendar',
  '/students',
  '/parents',
  '/staff',
  '/leave-management',
  '/transfer-certificates',
  '/attendance',
  '/exams',
  '/assignments',
  '/online-exams',
  '/report-cards',
  '/co-scholastic',
  '/program-candidates',
  '/notifications',
  '/announcements',
  '/sliders',
  '/affidavits',
  '/timetable',
  '/fees',
  '/visitors',
  '/visits',
  '/settings',
  '/roles',
  '/reports',
  '/mis-reports',
  '/id-cards',
  '/online-admissions',
  '/pgc-registrations',
  '/material-requests',
  '/guardian-attendance',
  '/noorani-attendance',
  '/noorani-tutorials',
  '/ho-fees-share',
  '/quran-tracker',
];

/** Longest module key that prefixes the given pathname, or null. */
export function moduleForPath(pathname: string): string | null {
  let best: string | null = null;
  for (const k of MODULE_KEYS) {
    if (pathname === k || pathname.startsWith(k + '/')) {
      if (!best || k.length > best.length) best = k;
    }
  }
  return best;
}

export interface AccessCtx {
  isAdmin: boolean;
  permissions: Set<string>;
}

/** Can the user view/navigate this path? */
export function canAccessPath(ctx: AccessCtx, pathname: string): boolean {
  if (ctx.isAdmin) return true;
  if (pathname === '/' || pathname === '/dashboard' || pathname.startsWith('/dashboard/'))
    return true;
  const mod = moduleForPath(pathname);
  if (!mod) return false; // unknown / admin-only modules (library, reports…)
  return ctx.permissions.has(`${mod}:list`);
}

/** Can the user perform an action on a module? */
export function can(
  ctx: AccessCtx,
  moduleKey: string,
  action: PermAction,
): boolean {
  if (ctx.isAdmin) return true;
  return ctx.permissions.has(`${moduleKey}:${action}`);
}
